/**
 * Cache Manager - Global singleton for client-side caching of API responses
 * CACHING ENABLED globally - automatically caches GET requests
 */

const CACHE_PREFIX = 'venturepoint_cache_';
const CACHE_EXPIRY_PREFIX = 'venturepoint_expiry_';
const DEFAULT_TTL = 3600; // 1 hour in seconds
const CACHING_ENABLED = true; // GLOBAL CACHE ENABLE SWITCH

// Cache configuration for different endpoint types
const CACHE_CONFIG = {
  '/api/projects': { ttl: 3600, maxSize: 100 }, // 1 hour
  '/api/projects/:id': { ttl: 3600, maxSize: 100 },
  '/api/partners': { ttl: 3600, maxSize: 100 },
  '/api/partners/:id': { ttl: 3600, maxSize: 100 },
  '/api/advisors': { ttl: 3600, maxSize: 100 },
  '/api/advisors/:id': { ttl: 3600, maxSize: 100 },
  '/api/authors': { ttl: 3600, maxSize: 100 },
  '/api/articles': { ttl: 1800, maxSize: 100 }, // 30 minutes
  '/api/events': { ttl: 1800, maxSize: 100 },
  '/api/events/:id': { ttl: 1800, maxSize: 100 },
  '/api/services': { ttl: 3600, maxSize: 100 },
  '/api/team': { ttl: 3600, maxSize: 100 },
  '/api/team/:id': { ttl: 3600, maxSize: 100 },
  '/api/about': { ttl: 0, maxSize: 0 }, // Disable caching for about page
  '/api/auth/me': { ttl: 600, maxSize: 10 }, // 10 minutes for user data
  '/api/contact': { ttl: 0, maxSize: 0 }, // Disable caching for contact messages (dynamic content)
};

class CacheManager {
  constructor() {
    this.memoryCache = new Map();
    this.listeners = new Set(); // Event listeners for cache changes
    this.stats = {
      hits: 0,
      misses: 0,
      clears: 0,
    };
  }

  /**
   * Subscribe to cache invalidation events
   */
  subscribe(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback); // Unsubscribe function
  }

  /**
   * Notify all listeners of cache invalidation
   */
  notify(endpoint) {
    this.listeners.forEach(callback => callback(endpoint));
  }

  /**
   * Get cache configuration for an endpoint
   */
  getConfig(endpoint) {
    // Try exact match first
    if (CACHE_CONFIG[endpoint]) {
      return CACHE_CONFIG[endpoint];
    }

    // Try pattern match (e.g., /api/projects/:id)
    const patterns = Object.keys(CACHE_CONFIG);
    for (const pattern of patterns) {
      if (pattern.includes(':id')) {
        const regex = new RegExp('^' + pattern.replace(':id', '[^/]+') + '$');
        if (regex.test(endpoint)) {
          return CACHE_CONFIG[pattern];
        }
      }
    }

    // Default config
    return { ttl: DEFAULT_TTL, maxSize: 100 };
  }

  /**
   * Generate cache key
   */
  getCacheKey(endpoint, params = null) {
    let key = CACHE_PREFIX + endpoint;
    if (params) {
      key += '_' + JSON.stringify(params).replace(/[\s"]/g, '');
    }
    return key;
  }

  /**
   * Set cache item - DISABLED
   */
  set(endpoint, data, params = null) {
    if (!CACHING_ENABLED) return; // Caching disabled globally
    
    const key = this.getCacheKey(endpoint, params);
    const config = this.getConfig(endpoint);
    const expiryKey = CACHE_EXPIRY_PREFIX + key;
    
    // Store in memory cache
    this.memoryCache.set(key, {
      data,
      timestamp: Date.now(),
      ttl: config.ttl,
    });

    // Also store in localStorage for persistence
    try {
      const expiryTime = Date.now() + config.ttl * 1000;
      localStorage.setItem(key, JSON.stringify(data));
      localStorage.setItem(expiryKey, expiryTime.toString());
    } catch (error) {
      // localStorage might be full or unavailable
      console.warn('Failed to cache to localStorage:', error);
    }
  }

  /**
   * Get cache item - DISABLED (returns null)
   * @param {string} endpoint - The endpoint to get cache for
   * @param {Object|null} paramsOrOptions - Legacy: params object, or options object with { allowStale: true }
   * @returns {*} Always returns null (caching disabled)
   */
  get(endpoint, paramsOrOptions = null) {
    if (!CACHING_ENABLED) return null; // Caching disabled globally
    
    // Support both old signature (params) and new signature (options)
    let params = null;
    let allowStale = false;
    
    if (paramsOrOptions && typeof paramsOrOptions === 'object' && !Array.isArray(paramsOrOptions)) {
      if ('allowStale' in paramsOrOptions) {
        // New signature with options
        allowStale = paramsOrOptions.allowStale;
      } else {
        // Old signature with params
        params = paramsOrOptions;
      }
    }

    const key = this.getCacheKey(endpoint, params);
    const expiryKey = CACHE_EXPIRY_PREFIX + key;
    
    // Check memory cache first
    const memCached = this.memoryCache.get(key);
    if (memCached) {
      const age = Date.now() - memCached.timestamp;
      if (age < memCached.ttl * 1000) {
        // Fresh cache
        this.stats.hits++;
        return memCached.data;
      } else if (allowStale) {
        // Cache is expired but allowStale is true - serve it anyway
        console.log(`[CacheManager] Serving stale data from memory for ${endpoint}`);
        this.stats.hits++;
        return memCached.data;
      } else {
        // Cache is expired and not allowing stale - remove it
        this.memoryCache.delete(key);
      }
    }

    // Check localStorage
    try {
      const expiryTime = localStorage.getItem(expiryKey);
      const data = localStorage.getItem(key);
      
      if (data) {
        const parsed = JSON.parse(data);
        const isExpired = expiryTime && Date.now() >= parseInt(expiryTime);
        
        if (!isExpired) {
          // Fresh cache in localStorage
          this.memoryCache.set(key, {
            data: parsed,
            timestamp: Date.now(),
            ttl: this.getConfig(endpoint).ttl,
          });
          this.stats.hits++;
          return parsed;
        } else if (allowStale) {
          // Cache is expired but allowStale is true
          console.log(`[CacheManager] Serving stale data from localStorage for ${endpoint}`);
          this.memoryCache.set(key, {
            data: parsed,
            timestamp: Date.now() - this.getConfig(endpoint).ttl * 1000, // Mark as stale in memory too
            ttl: this.getConfig(endpoint).ttl,
          });
          this.stats.hits++;
          return parsed;
        } else {
          // Expired, remove it
          localStorage.removeItem(key);
          localStorage.removeItem(expiryKey);
        }
      }
    } catch (error) {
      console.warn('Failed to retrieve from localStorage:', error);
    }

    this.stats.misses++;
    return null;
  }

  /**
   * Check if cache exists and is valid
   */
  has(endpoint, params = null) {
    return this.get(endpoint, params) !== null;
  }

  /**
   * Clear specific endpoint cache
   */
  clear(endpoint, params = null) {
    const key = this.getCacheKey(endpoint, params);
    const expiryKey = CACHE_EXPIRY_PREFIX + key;
    
    this.memoryCache.delete(key);
    
    try {
      localStorage.removeItem(key);
      localStorage.removeItem(expiryKey);
    } catch (error) {
      console.warn('Failed to clear localStorage:', error);
    }

    this.stats.clears++;
    
    // Notify all listeners that this endpoint's cache was cleared
    this.notify(endpoint);
  }

  /**
   * Clear all caches
   */
  clearAll() {
    this.memoryCache.clear();
    
    try {
      const keys = Object.keys(localStorage);
      keys.forEach((key) => {
        if (key.startsWith(CACHE_PREFIX) || key.startsWith(CACHE_EXPIRY_PREFIX)) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.warn('Failed to clear all localStorage:', error);
    }

    this.stats.clears++;
    
    // Notify all listeners that all caches were cleared
    this.notify('*');
  }

  /**
   * Get cache statistics
   */
  getStats() {
    const total = this.stats.hits + this.stats.misses;
    const hitRate = total > 0 ? ((this.stats.hits / total) * 100).toFixed(2) : 0;
    
    return {
      ...this.stats,
      total,
      hitRate: `${hitRate}%`,
    };
  }

  /**
   * Get metadata about a cache entry (for debugging)
   * @param {string} endpoint
   * @returns {Object|null} { exists, source, timestamp, ttl, ageMs }
   */
  getMeta(endpoint) {
    const key = this.getCacheKey(endpoint);
    const expiryKey = CACHE_EXPIRY_PREFIX + key;

    // Check memory cache
    const mem = this.memoryCache.get(key);
    if (mem) {
      return {
        exists: true,
        source: 'memory',
        timestamp: mem.timestamp,
        ttl: mem.ttl,
        ageMs: Date.now() - mem.timestamp,
      };
    }

    // Check localStorage
    try {
      const expiryTime = localStorage.getItem(expiryKey);
      const data = localStorage.getItem(key);
      if (data) {
        const parsedExpiry = expiryTime ? parseInt(expiryTime) : null;
        return {
          exists: true,
          source: 'localStorage',
          timestamp: parsedExpiry ? parsedExpiry - this.getConfig(endpoint).ttl * 1000 : null,
          ttl: this.getConfig(endpoint).ttl,
          ageMs: parsedExpiry ? (Date.now() - (parsedExpiry - this.getConfig(endpoint).ttl * 1000)) : null,
          expiresAt: parsedExpiry || null,
        };
      }
    } catch (err) {
      // ignore
    }

    return null;
  }

  /**
   * Clear statistics
   */
  clearStats() {
    this.stats = {
      hits: 0,
      misses: 0,
      clears: 0,
    };
  }

  /**
   * Invalidate cache by pattern (useful for updates)
   */
  invalidatePattern(pattern) {
    const regex = new RegExp(pattern);
    
    // Clear memory cache
    for (const key of this.memoryCache.keys()) {
      if (regex.test(key)) {
        this.memoryCache.delete(key);
      }
    }

    // Clear localStorage
    try {
      const keys = Object.keys(localStorage);
      keys.forEach((key) => {
        if (key.startsWith(CACHE_PREFIX) && regex.test(key)) {
          const expiryKey = CACHE_EXPIRY_PREFIX + key;
          localStorage.removeItem(key);
          localStorage.removeItem(expiryKey);
        }
      });
    } catch (error) {
      console.warn('Failed to invalidate cache pattern:', error);
    }
  }
}

// Export singleton instance
export const cacheManager = new CacheManager();

export default cacheManager;
